import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import {
  EmphasizedNumber,
  IconButton,
  Icons,
  InformationModal,
  ItemImageArea,
} from 'utoniq-ui'

import {
  ConsoleCollectionItemDetailFragment,
  ItemSelectBoxFragment,
  ItemType,
  SalesStatus,
} from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ItemIcon } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'
import { generateFullPath } from '@/lib/url'

import { ItemStatusCell } from './ItemStatusCell'

export type ItemSelectBoxProps = {
  item: ItemSelectBoxFragment | ConsoleCollectionItemDetailFragment['item']
  artistId?: string
  editable?: boolean
  showIcon?: boolean
  onToggleActive: (id: string, isActive: boolean) => Promise<void>
  onToggleSelling: (id: string, isSelling: boolean) => Promise<void>
  onRemove: (itemId: string) => Promise<void>
  menuLabel?: {
    deleteLabel?: string
  }
  removeItemConfirmModal?: {
    title: string
    content: string
    confirm: string
  }
}

export const ItemSelectBox: FC<ItemSelectBoxProps> = ({
  item,
  artistId,
  showIcon,
  editable,
  onToggleActive,
  onToggleSelling,
  onRemove,
  menuLabel,
  removeItemConfirmModal,
}) => {
  const { t } = useTranslation('consoleItems')
  const { generatePath, routerPages } = useRouter()
  const { showMessage } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const { onClick } = useBrowserLink(
    generatePath(routerPages.ArtistStoreItemDetail, {
      artistId: item.issuedArtist?.id ?? artistId ?? '#',
      itemId: item.id,
    })
  )
  const [open, setOpen] = useState<boolean>(false)
  const [removing, setRemoving] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const options = [
    {
      label: item.isPublished ? t('keepPrivateLabel') : t('publishLabel'),
      async onClick() {
        setAnchorEl(null)
        await onToggleActive(item.id, item.isPublished)
      },
    },
    {
      label:
        item.salesStatus === SalesStatus.Active
          ? t('stopSellingLabel')
          : t('startSellingLabel'),
      async onClick() {
        setAnchorEl(null)
        await onToggleSelling(item.id, item.salesStatus !== SalesStatus.Stopped)
      },
    },
    {
      label: t('copyURLLabel'),
      async onClick() {
        await copy(
          generateFullPath(routerPages.ArtistStoreItemDetail, {
            artistId: item.issuedArtist?.id ?? artistId ?? '#',
            itemId: item.id,
          })
        )
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
    {
      label: t('openStoreLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClick()
        setAnchorEl(null)
      },
    },
    {
      label: menuLabel?.deleteLabel ?? t('deleteLabel'),
      onClick() {
        setOpen(true)
        setAnchorEl(null)
      },
    },
  ]

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleRemove = useCallback(async () => {
    setRemoving(true)
    await onRemove(item.id).finally(() => {
      setOpen(false)
      setRemoving(false)
    })
  }, [onRemove, item.id])

  return (
    <Card elevation={0} sx={{ position: 'relative' }}>
      <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
        <Stack direction="row" spacing={4}>
          <Stack sx={{ position: 'relative' }}>
            {showIcon && (
              <Stack
                sx={{
                  zIndex: 1,
                  marginInline: 'auto',
                  pl: 1,
                  width: 104,
                  position: 'absolute',
                  top: 8,
                }}
              >
                <ItemIcon type={item.type} />
              </Stack>
            )}
            <ItemImageArea
              size="small"
              src={item.thumbnail?.front?.smallUri ?? ''}
            />
            {/**
             * @description
             * Open when function is implemented in the future
             * https://app.clickup.com/t/908127/UT-5837
             */}
            {config.modules.nft && false && (
              <Icons.Nft
                fontSize="small"
                sx={{
                  position: 'absolute',
                  color: 'secondary.dark',
                  bottom: 4,
                  right: 4,
                }}
              />
            )}
          </Stack>
          <Stack flex={1} justifyContent="center">
            <ItemStatusCell
              isPremium={item.isPremium}
              isPublished={item.isPublished}
              salesCondition={item.salesCondition}
              isDrm={item.isDrm}
            />
            <Typography variant="h6" sx={{ ...styleSets.lineCamp(2) }}>
              {item.title}
            </Typography>
            <Stack direction="row" sx={{ pt: 1 }}>
              <Stack flex={1}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('remainingQuantity', {
                      quantity: item.formattedRemainingNumber,
                    })}
                    {item.type === ItemType.Ticket && (
                      <>
                        <br />
                        {t('usedNumber', {
                          usedNumber: item.usedNumber ?? 0,
                        })}
                        /{item.purchasedNumber ?? 0}
                      </>
                    )}
                  </Typography>

                  {item.skuSettings && (
                    <ItemStatusTagCell
                      number={item.skuSettings?.units?.length || 0}
                    />
                  )}
                </Stack>
                {!!item.price && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {item.paymentType === 'prepaid_point' ||
                    !item.paymentType ? (
                      <>
                        <Icons.Point fontSize="small" />
                        <EmphasizedNumber variant="h5" color="text.secondary">
                          {item.formattedPrice}
                        </EmphasizedNumber>
                      </>
                    ) : (
                      <EmphasizedNumber variant="h5">
                        {t('price', { price: item.formattedPrice })}
                      </EmphasizedNumber>
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack spacing={3}>
            <IconButton.More
              size="xSmall"
              bgcolor="grey.100"
              color="grey.700"
              onClick={handleClick}
              sx={{
                position: 'relative',
                zIndex: 2,
                transform: 'rotate(90deg)',
              }}
            />
            {editable && (
              <IconButton.Pencil
                href={generatePath(routerPages.ConsoleItemEdit, {
                  itemId: item.id,
                })}
                size="xSmall"
                bgcolor="grey.100"
                color="grey.700"
                sx={{ position: 'relative', zIndex: 2 }}
              />
            )}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {options.map(({ icon, label, ...option }) => (
                <MenuItem {...option} sx={{ width: 200 }} key={label}>
                  {icon}
                  {label}
                </MenuItem>
              ))}
            </Menu>
            {/**
             * @description
             * アイテム編集機能が完成するまで暫定的に編集ボタンを非表示とする
             * https://app.clickup.com/t/908127/UT-8250
             */}
            {/* <IconButton.Pencil
              size="xSmall"
              bgcolor="grey.100"
              color="grey.700"
              href={generatePath(routerPages.ConsoleItemEdit, {
                itemId: item.id,
              })}
              sx={{ position: 'relative', zIndex: 2 }}
            /> */}
          </Stack>
        </Stack>
      </CardContent>
      <CardActionArea
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        }}
        href={generatePath(routerPages.ConsoleItemDetail, {
          itemId: item.id,
        })}
      />
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={removeItemConfirmModal?.title ?? t('deleteItemModalTitle')}
        content={removeItemConfirmModal?.content ?? t('deleteItemModalDesc')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            color="primary"
            loading={removing}
            sx={{ minWidth: 212 }}
            onClick={handleRemove}
          >
            {removeItemConfirmModal?.confirm ?? t('delete')}
          </Button>
        }
      />
    </Card>
  )
}

type ItemStatusTagCellProps = {
  number: number
}
const ItemStatusTagCell: Cell<ItemStatusTagCellProps> = ({ number }) => (
  <Button
    variant="outlined"
    size="small"
    disableRipple
    sx={{ pointerEvents: 'none' }}
  >
    <Typography variant="overline" color="text.hint">
      SKU（{number}）
    </Typography>
  </Button>
)
